import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
	COLLABORATIVE_CULMINATING_MOMENT,
	SPECIAL_EFFECT_IDS as SOUNDS,
} from '@mission.io/mission-toolkit/constants'
import { type CollaborativeCulminatingMomentScreen } from '@mission.io/mission-toolkit'

import {
	useJuniorPlusStateSelector,
	useMissionStateSelector,
} from '../../../../context/customHooks'
import {
	CulminatingMomentHeader,
	CollaborativeCulminatingMomentWrapper,
} from '../sharedComponents'
import { getLayout } from './MultiCanvasDisplay'
import { TeamCanvasDisplay } from './MultiCanvasDisplay'
import {
	to,
	useSpringRef,
	animated,
	useSprings,
	type SpringValue,
	useChain,
	useSpring,
} from '@react-spring/web'
import Confetti from 'react-confetti-boom'
import {
	MultiCanvasDisplay,
	CanvasContainer,
	TeamHeader,
} from './MultiCanvasDisplay'
import { getTeams } from '../../../../context/missionState/missionStats'
import { getConnectedStudents } from '@mission.io/mission-toolkit'
import { useTimer } from '../../../../context/timers'
import {
	useLocalDispatch,
	startLocalSoundEffect,
	fadeOutLocalSoundEffect,
} from '../../../../context'

const AnimatedTeamCanvasDisplay = animated(TeamCanvasDisplay)
const AnimatedCulminatingMomentHeader = animated(CulminatingMomentHeader)

const INITIAL_DELAY = 500
const DELAY_PER_MOVEMENT = 750
/**
 *  The reveal for a collaborative culminating moment vote experience.
 *  Meant to feel dramatic and reveal to a class which canvas received the most votes, and whether it was correct
 * @param {{[string]: string}} props.documentIdToTeamId a map to enable lookup of teamId from documentId
 * @param {Props['screen.currentStep']} props.screen.currentStep the current display step
 * @param {string} props.canvasId the canvasId to display the media on
 * @param {(documentId: string[]) => void} props.onComplete a callback to call when the media has finished displaying
 */
export function CollaborativeCulminatingVoting({
	screen,
	qualifiedDocuments,
	onComplete,
}: {
	screen: CollaborativeCulminatingMomentScreen,
	qualifiedDocuments: Array<{ documentId: string }>,
	onComplete: () => void,
}): React$Node {
	const connectedStudents = Object.keys(
		useMissionStateSelector(getConnectedStudents)
	)
	const votes =
		screen.currentStep.status ===
		COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.VOTING
			? screen.currentStep.votes
			: undefined

	let studentsWhoHaveVoted = 0
	let maxStudentsWhichCanVote = connectedStudents.length
	connectedStudents.forEach(studentId => {
		if (votes?.students[studentId]?.documentIds.length) {
			studentsWhoHaveVoted += 1
		}
	})

	const teams = useJuniorPlusStateSelector(getTeams)

	const screenTimer = useTimer(screen.timerId)
	const canvas = useMissionStateSelector(
		// $FlowFixMe[prop-missing] TODO - creativeCanvas on the junior state in mission-toolkit does not have `canvases` and is not exact, so we get a flow error here
		state => state?.stationData.creativeCanvas.canvases?.[screen.canvasId]
	)
	const documentIdToTeamId = useMemo(() => {
		if (!canvas) return {}
		const documentIdToTeamId = {}

		for (const team of canvas.teams) {
			documentIdToTeamId[team.document.id] = team.teamId
		}

		return documentIdToTeamId
	}, [canvas])

	const winningDocumentId =
		screen.currentStep.status ===
		COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS
			? screen.currentStep.currentMedia[0]?.documentId
			: screen.currentStep.status ===
			  COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.GRADING_HIDDEN
			? screen.currentStep.qualifiedDocuments[0]?.documentId
			: null
	const shouldShowConfetti =
		screen.currentStep.status ===
		COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS

	const [showConfetti, setShowConfetti] = useState(false)

	const distancesRef = useRef([])
	const indexOfWinner = qualifiedDocuments.findIndex(
		({ documentId }) => documentId === winningDocumentId
	)

	// This effect gets us the dimensions of the winning canvas so we can center it
	useEffect(() => {
		const getDistancesForEachElementToTravel = () => {
			distancesRef.current = qualifiedDocuments.map(({ documentId }, i) => {
				const elementRect = document
					.getElementById(`canvas-document-container-${documentId}`)
					?.getBoundingClientRect()

				if (documentId === winningDocumentId) {
					const containerRect = document
						.getElementById('multi-canvas-container')
						?.getBoundingClientRect()
					return elementRect && containerRect
						? getDistanceForElementToCenter(elementRect, containerRect)
						: { x: 0, y: 0 }
				} else {
					return elementRect
						? getDistanceForLeavingElements(
								elementRect,
								{
									width: window.innerWidth,
									height: window.innerHeight,
								},
								i,
								getLayout(qualifiedDocuments.length)
						  )
						: { x: -1000, y: 0 }
				}
			})
		}
		window.addEventListener('resize', getDistancesForEachElementToTravel)
		getDistancesForEachElementToTravel()
		return () => {
			window.removeEventListener('resize', getDistancesForEachElementToTravel)
		}
	}, [winningDocumentId, qualifiedDocuments])

	const [endOfTranslations, setEndOfTranslations] = useState(false)

	const [springs] = useSprings(
		qualifiedDocuments.length,
		i => {
			const { x, y } = distancesRef.current[i] || { x: 0, y: 0 }
			const args: {
				from: { x: number, y: number, opacity?: number },
				to: { x: number, y: number, opacity?: number },
				delay?: number,
				onRest?: () => void,
				onResolve?: () => void,
				config?: {},
				immediate?: boolean,
			} = {
				from: { x: 0, y: 0 },
				to: { x, y },
				cancel: !winningDocumentId,
			}
			if (i === indexOfWinner) {
				args.delay =
					INITIAL_DELAY + (qualifiedDocuments.length - 1) * DELAY_PER_MOVEMENT
				// If there is only 1 document to display, the document will already be centered so there is no need to animate
				if (qualifiedDocuments.length === 1) {
					args.onResolve = () => setEndOfTranslations(true)
					args.immediate = true
				} else {
					args.onRest = () => setEndOfTranslations(true)
				}
			} else {
				args.from.opacity = 1
				args.to.opacity = 0
				args.delay =
					INITIAL_DELAY + (i > indexOfWinner ? i - 1 : i) * DELAY_PER_MOVEMENT
				args.config = { tension: 280, friction: 120 } // basically a slow spring
			}
			return args
		},
		[distancesRef.current]
	)
	const dispatch = useLocalDispatch()

	const onCompleteRef = useRef(onComplete)
	useEffect(() => {
		onCompleteRef.current = onComplete
	}, [onComplete])

	useEffect(() => {
		if (endOfTranslations) {
			dispatch(startLocalSoundEffect(SOUNDS.APPLAUSE))
			let fadeOutApplauseTimeout = null
			const confettiTimeout = setTimeout(() => {
				if (shouldShowConfetti) {
					setShowConfetti(true)
				}
				fadeOutApplauseTimeout = setTimeout(() => {
					dispatch(fadeOutLocalSoundEffect(SOUNDS.APPLAUSE))
					onCompleteRef.current()
				}, 3000)
			}, 1200) // this value must be longer than the transition duration below on the AnimatedTeamCanvasDisplay
			return () => {
				clearTimeout(confettiTimeout)
				// If the timeout is being cancelled before it runs, we need to fade out the sound effect if its running
				clearTimeout(fadeOutApplauseTimeout)
				dispatch(fadeOutLocalSoundEffect(SOUNDS.APPLAUSE))
			}
		}
	}, [endOfTranslations, shouldShowConfetti, dispatch])

	const fadeRef = useSpringRef()
	const fadeSpring = useSpring({
		ref: fadeRef,
		from: { opacity: 1 },
		to: { opacity: endOfTranslations ? 0 : 1 },
	})
	const shrinkRef = useSpringRef()
	const shrinkSpring = useSpring({
		ref: shrinkRef,

		from: { maxHeight: 100, paddingTop: 24, paddingBottom: 24, marginTop: 0 },
		to: {
			maxHeight: 0,
			paddingTop: 0,
			marginTop: 12,
			paddingBottom: 0,
		},
		cancel: !endOfTranslations,
	})

	useChain([fadeRef, shrinkRef], [0.3, 0.6])

	return (
		<CollaborativeCulminatingMomentWrapper>
			<AnimatedCulminatingMomentHeader
				style={shrinkSpring}
				timerStyles={fadeSpring}
				timeLeft={screenTimer?.timeLeft}
				rightChild={
					<animated.div style={fadeSpring}>
						{`${studentsWhoHaveVoted}/${maxStudentsWhichCanVote}`}
					</animated.div>
				}>
				<animated.div style={fadeSpring}>
					{screen.currentStep.status ===
					COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.VOTING
						? 'Voting in progress'
						: 'Votes are in!'}
				</animated.div>
			</AnimatedCulminatingMomentHeader>

			<MultiCanvasDisplay className="px-6 relative" id="multi-canvas-container">
				{springs.map(
					(
						{ x, y, ...otherStyles }: { x: SpringValue, y: SpringValue },
						index
					) => {
						const documentId = qualifiedDocuments[index].documentId
						const team = teams?.find(
							({ id }) => id === documentIdToTeamId[documentId]
						)
						const isWinner = index === indexOfWinner

						return (
							<AnimatedTeamCanvasDisplay
								id={`canvas-document-container-${documentId}`}
								className={
									endOfTranslations && isWinner
										? '!max-w-full !max-h-full w-full transition-[max-height] duration-1000 !absolute top-1/2 left-1/2 !-translate-x-1/2 !-translate-y-1/2'
										: ''
								}
								key={documentId}
								style={{
									...otherStyles,
									transform: to([x, y], (x, y, s) => {
										return isWinner
											? `translate(${x}px, ${y}px)`
											: `translate(${x}%, ${y}%)`
									}),
								}}>
								{team && <TeamHeader team={team} />}
								<CanvasContainer
									documentId={documentId}
									canvasId={screen.canvasId}
								/>
							</AnimatedTeamCanvasDisplay>
						)
					}
				)}
			</MultiCanvasDisplay>
			{showConfetti && (
				<div className="absolute inset-0 w-full h-full z-[3]">
					<Confetti
						mode="boom"
						deg="90"
						x={0.5}
						y={0.2}
						spreadDeg="45"
						particleCount={300}
						colors={['#05b2ae', '#39fdc1', '#817abc', '#00ffff', '#f7931e']}
					/>
				</div>
			)}
		</CollaborativeCulminatingMomentWrapper>
	)
}

// Figures out the x y translate distance for an element to leave the screen.
function getDistanceForLeavingElements(elem, screenDimensions, i, layout) {
	let plane = i % 2 === 0 ? 'X' : 'Y'

	const { rows, cols } = layout
	const row = Math.floor(i / cols)
	const col = i % cols
	const rowHalf = row < rows / 2 ? 'top' : 'bottom'
	const colHalf = col < cols / 2 ? 'left' : 'right'
	let direction
	let distance

	if (plane === 'X') {
		if (colHalf === 'left') {
			direction = -1
			distance = elem.left + elem.width
		} else {
			direction = 1
			distance = screenDimensions.width - elem.left + elem.width
		}
	} else {
		if (rowHalf === 'bottom') {
			direction = 1
			distance = screenDimensions.height - elem.top + elem.height
		} else {
			direction = -1
			distance = elem.top + elem.height
		}
	}
	return {
		x: plane === 'X' ? distance * direction : 0,
		y: plane === 'Y' ? distance * direction : 0,
	}
}

// Figures out the x y translate distance for an element to move to the center of a given container
function getDistanceForElementToCenter(elem, container) {
	const x = container.left + container.width / 2 - elem.width / 2
	const y = container.top + container.height / 2 - elem.height / 2

	return {
		x: Math.round(x - elem.left),
		y: Math.round(y - elem.top),
	}
}
